import { useModals } from "@/features/modals/hooks";
import {
  type UserTourState,
  type UserTourStateActions,
} from "@/features/tour/reducer";
import { JoyrideStyles } from "@/features/tour/styles";
import { setCompletedTour } from "@/features/tour/utils";
import { Button } from "@repo/skapa";
import { t } from "@repo/translation";
import { ACTIONS, EVENTS, type EventData, Joyride } from "react-joyride";

import { getCalendarTourSteps } from "./utils";

export const CalendarTour = ({
  state,
  actions,
}: {
  state: UserTourState;
  actions: UserTourStateActions;
}) => {
  const calendarTourSteps = getCalendarTourSteps();
  const { pushModal, hideModal } = useModals();

  const handleJoyrideCallback = (data: EventData) => {
    const { action, index, type } = data;
    const isCloseAction = action === ACTIONS.CLOSE;
    const isInitAction = action === ACTIONS.INIT;
    const isPrevAction = action === ACTIONS.PREV && type === EVENTS.STEP_AFTER;
    const isNextAction = action === ACTIONS.NEXT && type === EVENTS.STEP_AFTER;
    const isTargetNotFound =
      action === ACTIONS.NEXT && type === EVENTS.TARGET_NOT_FOUND;
    const isTargetNotFoundPrev =
      action === ACTIONS.PREV && type === EVENTS.TARGET_NOT_FOUND;
    const isTourEnd = type === EVENTS.TOUR_END;

    if (isCloseAction) {
      actions.setIsRunning(false);
      actions.setStep(calendarTourSteps.length - 1);
    }

    if (isInitAction) {
      actions.setIsRunning(true);
    }

    if (isPrevAction) {
      let delay = 200;
      if (index === 11) {
        const element = document.querySelector(calendarTourSteps[9]?.target);
        element?.scrollIntoView({ block: "center" });
      } else if (index === 8) {
        hideModal("TASK");
      } else if (index === 5) {
        actions.projectOverviewOpenModal(false);
      } else if (index === 7) {
        actions.projectOverviewOpenModal(true);
        window.scrollTo(0, 0);
      } else {
        delay = 0;
      }

      setTimeout(() => {
        actions.setStep(index - 1);
      }, delay);
    }

    if (isNextAction) {
      actions.setPreviousStep(false);
      if (index === 4) {
        actions.projectOverviewOpenModal(true);
        setTimeout(() => {
          const element = document.querySelector(calendarTourSteps[5]?.target);
          element?.scrollIntoView({ block: "center" });
          actions.setStep(index + 1);
        }, 200);
      } else if (index === 6) {
        actions.projectOverviewOpenModal(false);
        actions.setStep(index + 1);
      } else if (index === 7) {
        pushModal({
          type: "TASK",
          context: "CALENDAR",
        });
        setTimeout(() => {
          actions.setStep(index + 1);
        }, 200);
      } else if (index === 8) {
        setTimeout(() => {
          actions.setStep(index + 1);
        }, 200);
      } else {
        actions.setStep(index + 1);
      }
    }

    if (isTargetNotFoundPrev) {
      actions.setStep(index - 1);
    }

    if (isTargetNotFound) {
      actions.setStep(index + 1);
    }

    if (isTourEnd) {
      hideModal("TASK");
      actions.setIsRunning(false);
      setCompletedTour(state.currentTour);
    }
  };

  const shouldDisableScrolling = (stepIndex: number) => {
    return stepIndex === 7 || stepIndex === 8 || stepIndex === 9;
  };

  const stepsWithScrollBehavior = calendarTourSteps.map((step, stepIndex) => ({
    ...step,
    skipScroll: shouldDisableScrolling(stepIndex),
  }));

  return (
    <Joyride
      continuous
      stepIndex={state.currentStep}
      steps={stepsWithScrollBehavior}
      onEvent={handleJoyrideCallback}
      options={JoyrideStyles}
      locale={{
        back: <Button size="small">{t("label.back")}</Button>,
        last: (
          <Button size="small" type="primary">
            {t("label.end")}
          </Button>
        ),
        next: (
          <Button size="small" type="primary">
            {t("label.next")}
          </Button>
        ),
      }}
    />
  );
};
